import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Context as BloodDrawContext,
  BloodDrawSite,
  DEFAULT_DISTANCE_MI,
  DISTANCE_OPTIONS_MI,
} from "@app/provider/bloodDraw";
import { isPuertoRicoZipCode, isValidZip } from "@app/utils";
import { ZipValidationErrors } from "@app/components/forms";

// Re-exported for backward compatibility; callers should prefer importing
// from @app/provider/bloodDraw directly.
export { DEFAULT_DISTANCE_MI, DISTANCE_OPTIONS_MI };

const FETCH_DEBOUNCE_MS = 1000;

export interface UseLabLocatorArgs {
  prefillZip?: string;
}

export interface UseLabLocatorResult {
  zipCode?: string;
  setZipCode: (zip: string) => void;
  distance: number;
  setDistance: (miles: number) => void;
  sites?: BloodDrawSite[];
  isLoading: boolean;
  error: ZipValidationErrors | null;
  validate: (zip?: string) => ZipValidationErrors | null;
}

/**
 * UI concerns for the lab-locator screens (Public MP, Guest MP, Logged-In
 * MP). This hook does NOT hold state that survives its unmount — ZIP,
 * distance, sites, and the fetched-params dedupe signal all live in
 * `BloodDrawContext`. The hook owns:
 *   - format validation (Puerto Rico + ZIP regex)
 *   - debounced auto-fetch on (zipCode, distance) change
 *   - `ZipValidationErrors` local state machine
 *   - `prefillZip` seeding into context on first mount
 */
export const useLabLocator = ({
  prefillZip,
}: UseLabLocatorArgs = {}): UseLabLocatorResult => {
  const {
    bloodDrawSites,
    getBloodDrawSites,
    isLoading,
    zipCode,
    setZipCode,
    distance,
    setDistance,
    lastFetchedParams,
  } = useContext(BloodDrawContext);

  const [error, setError] = useState<ZipValidationErrors | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracks whether the fetch effect has run at least once. First run
  // (mount) fires the fetch synchronously — the user arrived at this
  // screen with a committed ZIP, they're not typing, so the 1s debounce
  // just adds a dead spinner. Subsequent runs (ZIP typing, distance
  // change) debounce as before.
  const hasMountedRef = useRef(false);
  // Mirror of context's lastFetchedParams so the dedupe check inside a
  // pending setTimeout callback reads the CURRENT value rather than the
  // one captured at schedule time. Matters when a fetch resolves and
  // updates lastFetchedParams while a debounced timer for a subsequent
  // keystroke is still pending — without this, the timer's dedupe would
  // use stale params and fire a redundant fetch.
  const lastFetchedParamsRef = useRef(lastFetchedParams);
  useEffect(() => {
    lastFetchedParamsRef.current = lastFetchedParams;
  }, [lastFetchedParams]);

  const validate = useCallback((zip?: string): ZipValidationErrors | null => {
    if (isPuertoRicoZipCode(zip)) return ZipValidationErrors.NO_LAB;
    if (!zip || zip.length === 0) return ZipValidationErrors.EMPTY;
    if (!isValidZip(zip)) return ZipValidationErrors.INVALID_ZIP_CODE;
    return null;
  }, []);

  // Seed prefillZip into context on first mount when context has no ZIP.
  // Runs exactly once — prefillZip is a first-render input, not a live prop.
  useEffect(() => {
    if (zipCode === undefined && prefillZip) {
      setZipCode(prefillZip);
    }
  }, []);

  useEffect(() => {
    // Clear-on-transition invariant: every time zipCode or distance changes,
    // this line resets `error` to the current validation result — which is
    // `null` when the ZIP is valid. That means every previous error state
    // (INVALID_ZIP_CODE, EMPTY, NO_LAB, FETCH_FAILED) is cleared here on
    // the next valid deps change before the next fetch fires. No separate
    // "reset error on success" call is needed anywhere else.
    const validationError = validate(zipCode);
    setError(validationError);
    if (validationError) return;

    // Guard against a late-resolving fetch mutating state after the effect
    // has re-run (user changed zipCode/distance mid-request) or the
    // component has unmounted. Cleanup flips `stale` so the async closure
    // below drops its result instead of calling setError on a superseded
    // or unmounted render.
    let stale = false;

    const fireFetch = async () => {
      // Dedupe at fire time using the freshest lastFetchedParams (via ref)
      // rather than the value captured at schedule time. Handles the case
      // where a prior fetch resolves during a pending debounce window.
      const current = lastFetchedParamsRef.current;
      if (
        bloodDrawSites !== undefined &&
        current?.location === zipCode &&
        current?.distance === distance
      ) {
        return;
      }
      const result = await getBloodDrawSites({
        location: zipCode as string,
        distance,
      });
      if (stale) return;
      if (!result.ok) {
        setError(ZipValidationErrors.FETCH_FAILED);
      }
    };

    // Fire immediately when it's a distance-only change on the same ZIP.
    // A miles-selector click is a discrete commit, not a keystroke burst,
    // so the 1s debounce would just add lag. Compares against
    // lastFetchedParams — if the ref is undefined (nothing fetched yet)
    // this falls to the debounce branch, which is safe.
    const cachedParams = lastFetchedParamsRef.current;
    const onlyDistanceChanged =
      cachedParams !== undefined &&
      cachedParams.location === zipCode &&
      cachedParams.distance !== distance;

    if (!hasMountedRef.current || onlyDistanceChanged) {
      // First fetch trigger of this hook instance, or a distance-only
      // change — fire immediately.
      hasMountedRef.current = true;
      fireFetch();
    } else {
      // ZIP typing (or any change without a prior successful fetch).
      // Debounce.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fireFetch, FETCH_DEBOUNCE_MS);
    }

    return () => {
      stale = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [zipCode, distance]);

  useEffect(() => {
    // Sets NO_LAB when a fetch has returned an empty result set. Two state
    // reads inside this effect are intentionally omitted from the deps
    // array:
    //   - `error`: adding it would create a re-render loop hazard the
    //     moment someone adds a `setError(null)` branch here. The `!error`
    //     read inside the check is a natural circuit-breaker: if a prior
    //     run already set NO_LAB, subsequent runs no-op without needing
    //     `error` as a trigger.
    //   - `zipCode`: this effect's decision is driven entirely by fetch
    //     state (bloodDrawSites/lastFetchedParams). Those two are updated
    //     atomically by the provider when a fetch resolves, and the
    //     re-run they trigger picks up the freshest `zipCode` from
    //     closure automatically. Adding `zipCode` here would only fire
    //     the effect on every keystroke of a ZIP change, which is
    //     wasted work — none of the check's outcomes hinge on `zipCode`
    //     changing without a corresponding fetch-state change.
    if (
      zipCode &&
      !isLoading &&
      !error &&
      bloodDrawSites !== undefined &&
      bloodDrawSites.length === 0 &&
      lastFetchedParams?.location === zipCode
    ) {
      setError(ZipValidationErrors.NO_LAB);
    }
  }, [bloodDrawSites, isLoading, lastFetchedParams]);

  return {
    zipCode,
    setZipCode,
    distance,
    setDistance,
    sites: bloodDrawSites,
    isLoading,
    error,
    validate,
  };
};

export default useLabLocator;
